import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SEO from "../components/SEO";
import { teamSrcSet } from "../lib/img";
import PageHero from "../components/PageHero";
import CTABand from "../components/CTABand";
import { BRAND, THERAPISTS, INSURANCES, CONTACT, IN_PERSON_NOTE } from "../data/site";
import { breadcrumbSchema, organizationSchema } from "../lib/schema";

const VALUES = [
  { title: "Compassion & respect", body: "Every client, every session — met without judgment." },
  { title: "Evidence-based care", body: "Proven approaches like CBT, DBT, EMDR, and exposure therapy." },
  { title: "Access for all of NJ", body: "Quality care shouldn't depend on your zip code or your commute." },
];

export default function About() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
  ];
  return (
    <>
      <SEO
        title="About NJ Therapy Online | Affiliated with Montville Therapy Group"
        description="NJ Therapy Online brings licensed, evidence-based telehealth counseling to all of New Jersey. Affiliated with Montville Therapy Group. Meet founder Dr. Reema Tajdar, LPC, PhD, ACS, and our team."
        path="/about"
        schema={[breadcrumbSchema(crumbs), organizationSchema()]}
      />
      <PageHero
        crumbs={crumbs}
        eyebrow="About us"
        title="Bringing trusted therapy to every corner of"
        accent="New Jersey"
        underline
        body="NJ Therapy Online makes high-quality, in-network mental health care easy to access — in a busy city, a shore town, or a rural township."
        testid="about-hero"
      />

      <section className="section" data-testid="about-affiliation">
        <div className="container-site grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <span className="eyebrow">Our connection</span>
            <h2 className="mt-3 text-3xl md:text-4xl">
              Affiliated with{" "}
              <a href={BRAND.affiliateUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 decoration-[var(--sage)] decoration-2 hover:text-[var(--sage)]" data-testid="about-affiliation-link">
                {BRAND.affiliate}
              </a>
            </h2>
            <p className="mt-5 text-body leading-relaxed">
              NJ Therapy Online is the statewide virtual practice affiliated with {BRAND.affiliate}, a boutique practice in Morris County. We share the same licensed clinicians, evidence-based approach, and compassionate care — now online for anyone in New Jersey.
            </p>
            <p className="mt-3 text-sm text-body" data-testid="about-inperson-note">{IN_PERSON_NOTE}</p>
          </div>
          <div className="grid gap-4">
            {VALUES.map((v) => (
              <div key={v.title} className="card p-6">
                <h3 className="text-lg">{v.title}</h3>
                <p className="mt-1.5 text-sm text-body">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="team" className="section bg-white border-y border-[var(--line)]" data-testid="about-team">
        <div className="container-site flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="eyebrow">Our clinicians</span>
            <h2 className="mt-3 text-3xl md:text-4xl">Meet the team</h2>
            <p className="mt-3 text-body max-w-xl">Licensed, caring clinicians who meet you where you are.</p>
          </div>
          <Link to="/team" className="btn btn-secondary self-start md:self-auto" data-testid="about-team-link">
            Meet the team <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="container-site mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {THERAPISTS.map((t) => (
            <Link key={t.id} to={`/team#${t.id}`} className="card card-hover p-4 flex items-center gap-4" data-testid={`about-therapist-${t.id}`}>
              <img src={t.photo} srcSet={teamSrcSet(t.photo)} sizes="72px" alt={`${t.name}, ${t.credentials}`} width="72" height="72" className="h-[72px] w-[72px] rounded-xl object-cover object-top bg-[var(--sage-tint)]" loading="lazy" decoding="async" />
              <div>
                <div className="font-semibold text-[var(--ink)]">{t.name}, <span className="font-medium text-body">{t.credentials}</span></div>
                <div className="text-sm text-[var(--sage)]">{t.title}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="section" data-testid="about-insurance">
        <div className="container-site grid lg:grid-cols-2 gap-10">
          <div>
            <span className="eyebrow">Insurance</span>
            <h2 className="mt-3 text-3xl md:text-4xl">In-network & affordable</h2>
            <p className="mt-4 text-body leading-relaxed">
              We're in-network with most major plans and verify benefits before your first session. Private pay is also available.
            </p>
            <Link to="/contact" className="mt-6 btn btn-primary" data-testid="about-cta">
              Verify my insurance <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3 content-start">
            {INSURANCES.map((i) => (
              <li key={i} className="card p-4 flex items-center gap-3 text-[15px] font-medium">
                <Check className="h-4 w-4 text-[var(--sage)]" /> {i}
              </li>
            ))}
          </ul>
        </div>
        <div className="container-site mt-10 text-sm text-body">
          Questions? Call <a href={CONTACT.phoneHref} className="font-semibold text-[var(--ink)]">{CONTACT.phone}</a>.
        </div>
      </section>

      <CTABand />
    </>
  );
}
